import React, {
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import {
  extend, params, string, useDataModel, QueryContext,
  shape, bool, arrayOf
} from '@thd-nucleus/data-sources';
import { node } from 'prop-types';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { QuestionsAndAnswersDesktop } from '@thd-olt-component-react/questions-and-answers';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { AugmentedReality } from '@thd-olt-component-react/augmented-reality';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { Price } from '@thd-olt-component-react/price';
import { DisclaimerMessage } from '@thd-olt-component-react/disclaimer-message';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { SpecificationsDesktop } from '@thd-olt-component-react/specifications';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';
import { Link, Links, StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { SeoLinks } from '@thd-olt-component-react/seo-links';
import { MovingCalculator } from '@thd-olt-component-react/moving-calculator';
import { PIPRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { VirtualConsulting } from '@thd-olt-component-react/virtual-consulting';
import { PaintDeliveryBanner } from '@thd-olt-component-react/paint-delivery-banner';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { RatingsAndReviewsDesktopImporter } from '@thd-olt-component-react/ratings-and-reviews';
import { PackagesMini } from '@thd-olt-component-react/packages-mini';
import { ProductOverviewDesktop } from '@thd-olt-component-react/product-overview';
import { EducationalSummary } from '@thd-olt-component-react/educational-summary';
import { ItemRelatedGroupsDesktopImporter } from '@thd-olt-component-react/item-related-groups';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { RecsContext } from '~/@thd-olt-component-react/recommendations/dist/components/RecsContext';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar,
  shouldDisplayRequiredItems,
} from './product-utils';
import { HYDRATION_STANDARD_SCROLL_BUFFER } from '../../constants';
import '../../styles/product-page.scss';
import ZoneCard from './zone-card';

const ChildMap = ({ children }) => {
  return <Row><Col nopadding>{children}</Col></Row>;
};

ChildMap.propTypes = {
  children: node.isRequired,
};

export const Product = () => {
  declareContexts([RecsContext, QueryContext, ExperienceContext]);
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { channel, segment = '', isDebugMode } = ctx;
  const { itemId } = useParams();
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const [hideBecauseOfPackagesMini, setHideBecauseOfPackagesMini] = useState(false);
  const resx = {
    appId: 'homedepot01',
    maxProducts: 16,
    products: itemId,
    showATC: true,
    criteo: {
      version: 'model2'
    },
    event: 'product',
    carouselOptions: {
      fullScrollSkip: 2
    }
  };
  const [recommendationsState, setRecommendationsState] = useState({
    ready: false,
    schema: {
      pipInStock: false,
      pipSponsored: true,
      visuallySimilar_1_0: false,
      required_items: false,
      pipSem: false,
      pipSemMobile: false
    }
  });
  const organicSearchSchema = 'pipsem';

  const arRef = useRef(null);
  const handleARClick = () => {
    arRef.current.click();
  };

  const updateStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (!el) return;
    el.classList.remove('Header3-sticky-desktop');
  };

  useEffect(() => {
    updateStickyHeader();
  }, []);

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  useEffect(() => {
    if (productData) {
      setRecommendationsState({
        ready: true,
        schema: {
          ...recommendationsState.schema,
          pipInStock: shouldDisplayPipInStock(productData),
          pipSem: shouldDisplayReferrerRecommendations(),
          visuallySimilar_1_0: shouldDisplayVisuallySimilar(productData),
          required_items: shouldDisplayRequiredItems(productData)
        }
      });
    }
  }, [productData]);

  const viewMoreDetailsSectionClick = () => {
    if (typeof window !== 'undefined') {
      if (document.getElementById('product-overview') === null) {
        window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        setTimeout(() => {
          window.scrollTo({ top: document.getElementById('product-section-overview').offsetTop, behavior: 'smooth' });
        }, 2000);
      } else {
        window.scrollTo({ top: document.getElementById('product-overview').offsetTop, behavior: 'smooth' });
      }
    }
  };

  const isPaint = productData?.product?.info?.dotComColorEligible;

  return (
    <LayoutManager name="pip-wrapper" useContextForProps>
      <LayoutGroup name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </LayoutGroup>
      <LayoutGroup name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </LayoutGroup>
      <LayoutGroup name="pip-content">
        <RecommendationsProvider resx={resx} ready={recommendationsState.ready}>
          <Row className="isBound">
            <LayoutManager
              data
              useContextForProps
              input={{
                channel,
                itemId,
                isDebugMode,
                segment
              }}
              name="pip-master"
            >
              <LayoutGroup tag={Row} name="sticknav">
                <StickyNav itemId={itemId}>
                  <Links>
                    <Link href="#product-section-overview">Product Overview</Link>
                    <Link href="#product-section-key-feat">Specifications</Link>
                    <Link href="#product-section-qa">Questions & Answers</Link>
                    <Link href="#product-section-rr">Customer Reviews</Link>
                  </Links>
                </StickyNav>
              </LayoutGroup>
              <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="breadcrumb-productInfoBar-grp">
                <LayoutManager name="breadcrumb-productInfoBar" useContextForProps>
                  <LayoutGroup className="u__visible" tag={Col} sm="7" flatten name="breadcrumbs">
                    <Breadcrumbs
                      itemId={itemId}
                    />
                  </LayoutGroup>
                  <LayoutGroup tag={Col} sm="5" nopadding name="product-info-bar">
                    <div className="product-info-bar--hide-mobile u__flex-end">
                      <ProductInfoBar itemId={itemId} />
                    </div>
                  </LayoutGroup>
                </LayoutManager>
              </LayoutGroup>
              {(recommendationsState.schema.pipSem) && (
                <LayoutGroup tag={Row} id="recs:organic_search" name="recs:organic_search">
                  <Col nopadding>
                    <RecommendationsPlaceholder scheme={organicSearchSchema} hideRecommendation />
                  </Col>
                </LayoutGroup>
              )}
              <LayoutGroup tag={Row} name="zone-a">
                <Col nopadding={channel === 'desktop'}>
                  <Row>
                    <Col>
                      <Discontinued itemId={itemId} />
                    </Col>
                    <Col sm="7" flatten className="product--sticky">
                      <ZoneCard className="zone-card overflow u__clearfix">
                        <LayoutManager name="product-details-share" useContextForProps />
                        <Row>
                          <LayoutManager name="product-details-share" useContextForProps>
                            <LayoutGroup tag={Col} sm="12" name="product-details">
                              <ProductDetails itemId={itemId}>
                                <ProductDetails.Badge itemId={itemId} />
                                <ProductDetails.BrandCollection showBrandHyperLink />
                                <div className="product-details__badge-title--wrapper">
                                  <ProductDetails.Title />
                                </div>
                                <div className="product-details__favorites">
                                  <Sharebelt itemId={itemId}>
                                    <ShareFavorite showCircle />
                                  </Sharebelt>
                                </div>
                                <ProductDetails.Ratings
                                  showBadge
                                  target="#product-section-rr"
                                  offsetselector="#sticky-nav"
                                  qaselector="#product-section-qa"
                                />
                              </ProductDetails>
                            </LayoutGroup>
                          </LayoutManager>
                        </Row>
                        <LayoutGroup tag={Col} sm="12">
                          <MediaGalleryDesktop
                            itemId={itemId}
                            showLoader
                            onMediaClick={handleARClick}
                          />
                        </LayoutGroup>
                        <LayoutGroup tag={Col} name="sharebelt">
                          <Sharebelt horizontal itemId={itemId}>
                            <ShareBeltShareButton email />
                            <ShareBeltPrint />
                          </Sharebelt>
                        </LayoutGroup>
                      </ZoneCard>
                    </Col>
                    <Col sm="5" flatten>
                      <div className="overflow">
                        <Row>
                          <LayoutManager
                            name="buy-area"
                            useContextForProps
                            data
                            input={{
                              channel,
                              segment
                            }}
                          >
                            <LayoutGroup
                              tag={Col}
                              name="price"
                              className="zone-card overflow"
                            >
                              <Price
                                channel="desktop"
                                displayEachUom={false}
                                itemId={itemId}
                                large
                                disableRangePricing
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: true
                                }}
                              />
                            </LayoutGroup>

                            <LayoutGroup
                              tag={Col}
                              name="disclaimer-message"
                              className="zone-card overflow"
                            >
                              <DisclaimerMessage itemId={itemId} />
                            </LayoutGroup>

                            <LayoutGroup
                              tag={Col}
                              name="consumer-card-messaging"
                              className="zone-card overflow consumer-credit-messaging__card"
                            >
                              <ConsumerCardMessaging
                                itemId={itemId}
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: true
                                }}
                              />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card overflow" tag={Col} name="promotion-products">
                              <PromotionProducts itemId={itemId} />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card" tag={Col} name="salient">
                              <SalientPoints itemId={itemId} max={3}>
                                <SalientListItem>
                                  {/* eslint-disable-next-line */}
                                  <a className="u__default-link" href="#product-overview" onClick={viewMoreDetailsSectionClick}>View More Details</a>
                                </SalientListItem>
                              </SalientPoints>
                            </LayoutGroup>

                            <LayoutGroup className="zone-card overflow" tag={Col} name="aisle-bay">
                              <AisleBay
                                itemId={itemId}
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: false
                                }}
                              />
                            </LayoutGroup>

                            <LayoutGroup
                              tag={Col}
                              name="paint-delivery-banner"
                              className="zone-card"
                            >
                              <PaintDeliveryBanner />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card overflow" tag={Col} name="super-sku">
                              <SuperSku
                                itemId={itemId}
                                onChange={onChange}
                                large={isPaint}
                                centeredCards={isPaint}
                                disableMediaAndAvailability={isPaint}
                              />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card" tag={Col} name="order-sample">
                              <OrderSample itemId={itemId}>
                                Need a closer look?
                              </OrderSample>
                            </LayoutGroup>

                            <LayoutGroup tag={Col} flatten name="buybox">
                              <Buybox itemId={itemId} lmUseContextForProps sticky showCards />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card" tag={Col} name="moving-calculator">
                              <MovingCalculator itemId={itemId} />
                            </LayoutGroup>

                            <LayoutGroup className="zone-card overflow" tag={Col} name="gcc">
                              <GlobalCustomConfigurator itemId={itemId} />
                            </LayoutGroup>
                          </LayoutManager>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </LayoutGroup>
              <LayoutGroup tag={Row} name="augmented-reality">
                <ZoneCard>
                  <AugmentedReality
                    itemId={itemId}
                    instructions
                    forwardedRef={arRef}
                  />
                </ZoneCard>
              </LayoutGroup>
              <LayoutGroup tag={Row} name="virtual-consulting">
                <Col nopadding>
                  <VirtualConsulting itemId={itemId} />
                </Col>
              </LayoutGroup>
              {recommendationsState.schema.pipInStock && (
                <RecommendationsPlaceholder
                  scheme="pipinstock"
                  layoutGroup={{
                    name: 'pipinstock',
                    tag: ChildMap
                  }}
                />
              )}
              {recommendationsState.schema.required_items && (
                <RecommendationsPlaceholder
                  scheme="required_items"
                  layoutGroup={{
                    name: 'required_items',
                    tag: ChildMap
                  }}
                />
              )}
              <PackagesMini
                itemId={itemId}
                callOnRender={() => {
                  if (!hideBecauseOfPackagesMini) {
                    setHideBecauseOfPackagesMini(true);
                  }
                }}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              {!hideBecauseOfPackagesMini && (
                <FrequentlyBoughtTogether
                  itemId={itemId}
                  qctx={qctx}
                  layoutGroup={{
                    tag: Row,
                  }}
                  dynamic={{
                    pageType: 'pip'
                  }}
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard
                  }}
                />
              )}
              <SponsoredPipBanner
                pageContext={{
                  label: 'pip',
                  data: {
                    itemId,
                    treatment: 'A'
                  },
                }}
                layoutGroup={{
                  name: 'pip-sponsored-banner-A'
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  id: 'hydrated-sponsored-pip-banner-A'
                }}
              />
              <ProductOverviewDesktop
                channel={channel}
                itemId={itemId}
                qctx={qctx}
                revJet
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
              />
              <SponsoredPipBanner
                pageContext={{
                  label: 'pip',
                  data: {
                    itemId,
                    treatment: 'B'
                  },
                }}
                layoutGroup={{
                  name: 'pip-sponsored-banner-B'
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  id: 'hydrated-sponsored-pip-banner-B',
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
              <EducationalSummary
                itemId={itemId}
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid'
                }}
              />
              <ItemRelatedGroupsDesktopImporter
                accordion
                dsNext
                itemId={itemId}
                showMobileCardTabs
                maxItems={6}
                qctx={qctx}
                rowHeader="Get Everything You Need"
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              <RecommendationsPlaceholder
                defer
                scheme="article_recs"
                layoutGroup={{
                  name: 'article_recommendations',
                  tag: ChildMap
                }}
              />
              <RecommendationsPlaceholder
                defer
                scheme="pip_sponsored"
                layoutGroup={{
                  name: 'pip_sponsored',
                  tag: ChildMap
                }}
              />
              <FeatureBasedRecommendationsImporter
                hideOnMobile
                itemId={itemId}
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                hydrator={{
                  tag: ZoneCard,
                  className: 'grid'
                }}
                dynamic={{
                  pageType: 'pip'
                }}
              />
              <SpecificationsDesktop
                itemId={itemId}
                layoutGroup={{
                  tag: Row
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  tag: ZoneCard,
                  className: 'grid'
                }}
              />
              {(recommendationsState.ready && !recommendationsState.schema.pipInStock) && (
                <RecommendationsPlaceholder
                  defer
                  scheme="pip_alernatives"
                  layoutGroup={{
                    name: 'alternatives',
                    tag: ChildMap
                  }}
                />
              )}
              {recommendationsState.schema.visuallySimilar_1_0 && (
                <RecommendationsPlaceholder
                  defer
                  scheme="visuallysimilar_1_0"
                  layoutGroup={{
                    name: 'visually-similar',
                    tag: ChildMap
                  }}
                />
              )}
              <QuestionsAndAnswersDesktop
                itemId={itemId}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip',
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                }}
              />
              <RatingsAndReviewsDesktopImporter
                itemId={itemId}
                prosAndCons
                qctx={qctx}
                layoutGroup={{
                  tag: Row,
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard
                }}
              />
              <RecommendationsPlaceholder
                defer
                scheme="rv_gm_pip_rr"
                layoutGroup={{
                  name: 'recently-viewed',
                  tag: ChildMap
                }}
              />
              <RelatedSearch
                itemId={itemId}
                layoutGroup={{
                  tag: ChildMap
                }}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator
              />
              <SeoLinks
                path={pathname}
                layoutGroup={{
                  tag: Row
                }}
                dynamic={{
                  pageType: 'pip',
                }}
                hydrator={{
                  className: 'grid'
                }}
              />
            </LayoutManager>
          </Row>
        </RecommendationsProvider>
      </LayoutGroup>
    </LayoutManager>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {};

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      }),
      info: shape({
        dotComColorEligible: bool()
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          reqItems: bool()
        }),
        dotComColorEligible: bool()
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  RecommendationsPlaceholder,
  Discontinued,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  ConsumerCardMessaging,
  DisclaimerMessage,
  GlobalCustomConfigurator,
  OrderSample,
  SuperSku,
  MovingCalculator,
  Buybox,
  AugmentedReality,
  FrequentlyBoughtTogether,
  ProductOverviewDesktop,
  ItemRelatedGroupsDesktopImporter,
  FeatureBasedRecommendationsImporter,
  SpecificationsDesktop,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsDesktopImporter,
  EducationalSummary,
  SeoLinks,
  RelatedSearch,
  PromotionProducts,
  VirtualConsulting,
  PackagesMini
);
